import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiTrash2, FiRefreshCw, FiCode, FiX, FiSave, FiEdit2, FiDelete, FiAlertTriangle, FiCopy, FiSearch, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { faturamentoService, filterDataService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

import { BIMultiSelectSearch, BISingleSelectSearch, BIPagination } from '../common';
import Loading from '../common/Loading';
const MultiSelectSearch = BIMultiSelectSearch;
const SingleSelectSearch = BISingleSelectSearch;

function Variaveis() {
  const { hasSectionAccess } = useAuth();
  const canManage = hasSectionAccess('variaveis', 'manage');
  const [variables, setVariables] = useState([]);
  const [allVariables, setAllVariables] = useState([]);
  const [filters, setFilters] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showVariableModal, setShowVariableModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterBranchSearch, setFilterBranchSearch] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedVarIds, setSelectedVarIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Estados para modais de confirmação
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [variableToDelete, setVariableToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorModalTitle, setErrorModalTitle] = useState('Erro ao Salvar');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Debounce search term para evitar chamadas excessivas à API
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSort = (field) => {
    let newOrder = 'asc';
    if (sortField === field) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      newOrder = (field === 'id') ? 'desc' : 'asc';
    }
    setSortField(field);
    setSortOrder(newOrder);
    setPage(1);
    loadVariables({ page: 1, per_page: perPage, sort_by: field, sort_order: newOrder });
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <span style={{ marginLeft: 4, opacity: 0.2, fontSize: 10, userSelect: 'none' }}>▲▼</span>;
    return <span style={{ marginLeft: 4, fontSize: 10, userSelect: 'none' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
  };

  const [variableForm, setVariableForm] = useState({
    id: null,
    name: '',
    description: '',
    formula: '',
    commission_percent: '',
    commission_min_sold_value: '',
    commission_use_goal_value: false,
    show_commission_percentage: true,
    include_in_goal_total: true,
    include_commission_in_goal_total: true,
    available_for_goals: false,
    use_goal_filter_config: false,
    has_accrual_rule: false,
    accrual_rules: [],
    use_sold_qty_for_bonus: false,
    bonus_validation_qty: '',
    variable_bonus_amount: '',
    active: 1,
    branch_ids: [],
    filters: [],
    variable_deps: []
  });

  // Estados para autocomplete da fórmula
  const [showFormulaDropdown, setShowFormulaDropdown] = useState(false);
  const [formulaCursorPos, setFormulaCursorPos] = useState(0);
  const [formulaSearchTerm, setFormulaSearchTerm] = useState('');
  const formulaInputRef = React.useRef(null);

  // Estados para substituição de variáveis na fórmula
  const [showVariableReplaceDropdown, setShowVariableReplaceDropdown] = useState(false);
  const [selectedVariableToReplace, setSelectedVariableToReplace] = useState(null);
  const [variableReplacePosition, setVariableReplacePosition] = useState({ x: 0, y: 0 });

  const loadVariables = useCallback(async (customParams = {}) => {
    setLoading(true);
    try {
      const activePage = customParams.page !== undefined ? customParams.page : page;
      const activePerPage = customParams.per_page !== undefined ? customParams.per_page : perPage;
      const activeSortBy = customParams.sort_by !== undefined ? customParams.sort_by : sortField;
      const activeSortOrder = customParams.sort_order !== undefined ? customParams.sort_order : sortOrder;
      const activeSearch = customParams.search !== undefined ? customParams.search : debouncedSearchTerm;

      const query = {
        page: activePage,
        per_page: activePerPage,
        sort_by: activeSortBy,
        sort_order: activeSortOrder
      };

      if (activeSearch && String(activeSearch).trim() !== '') {
        query.search = String(activeSearch).trim();
      }

      const res = await faturamentoService.listVariables(query);
      setVariables(res?.data?.variables || []);
      setTotalRecords(res?.data?.total || 0);
    } catch (e) {
      setMessage({ type: 'danger', text: e.response?.data?.error || 'Erro ao listar variáveis' });
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sortField, sortOrder, debouncedSearchTerm]);

  const loadFilters = useCallback(async () => {
    try {
      const res = await faturamentoService.listSavedFilters({ active_only: 1 });
      setFilters(res?.data?.filters || []);
    } catch (e) {
      console.error('Erro ao carregar filtros:', e);
    }
  }, []);

  const loadReports = useCallback(async () => {
    try {
      const res = await faturamentoService.listReports({ per_page: 200 });
      setReports(res?.data?.reports || []);
    } catch (e) {
      console.error('Erro ao carregar relatorios:', e);
    }
  }, []);

  const loadBranches = useCallback(async () => {
    try {
      const res = await filterDataService.getFiliais();
      const filiaisData = res?.data?.filiais || [];
      setBranches(filiaisData.map(b => ({ id: String(b.codigo), label: `${b.codigo} - ${b.nomefantasia || b.razaosocial || 'Sem nome'}`, codigo: String(b.codigo) })));
    } catch (e) {
      console.error('Erro ao carregar filiais:', e);
    }
  }, []);

  useEffect(() => {
    loadVariables();
  }, [loadVariables]);

  useEffect(() => {
    loadFilters();
    loadReports();
    loadBranches();
  }, [loadFilters, loadReports, loadBranches]);

  const loadAllVariables = useCallback(async () => {
    try {
      const res = await faturamentoService.listVariables({ per_page: 0, active: 1 });
      setAllVariables(res?.data?.variables || []);
    } catch (e) {
      console.error('Erro ao carregar todas as variáveis:', e);
    }
  }, []);

  useEffect(() => {
    loadAllVariables();
  }, [loadAllVariables]);

  // Recarregar lista de filtros e variáveis ao abrir o modal
  useEffect(() => {
    if (showVariableModal) {
      loadFilters();
      loadAllVariables();
    }
  }, [showVariableModal, loadFilters, loadAllVariables]);

  // Consulta assíncrona de filtros diretamente na API
  const fetchFiltersForSelect = useCallback(async (search = '', limit = 100) => {
    try {
      const res = await faturamentoService.listSavedFilters({
        search: search || undefined,
        active_only: 1,
        per_page: limit || 100
      });
      const list = res?.data?.filters || [];
      setFilters(prev => {
        const map = new Map(prev.map(f => [f.id, f]));
        list.forEach(f => map.set(f.id, f));
        return Array.from(map.values());
      });
      return list;
    } catch (err) {
      console.error('Erro ao consultar filtros na API:', err);
      return [];
    }
  }, []);

  // Consulta assíncrona de variáveis diretamente na API
  const fetchVariablesForSelect = useCallback(async (search = '', limit = 100) => {
    try {
      const res = await faturamentoService.listVariables({
        search: search || undefined,
        active: 1,
        per_page: limit || 100
      });
      let list = res?.data?.variables || [];
      if (variableForm.id) {
        list = list.filter(v => v.id !== variableForm.id);
      }
      if (variableForm.branch_ids.length > 0) {
        list = list.filter(v => {
          const vBranches = (v.filter_branch || '').split(',').map(s => s.trim()).filter(Boolean);
          return vBranches.length === 0 || vBranches.some(code => variableForm.branch_ids.includes(code));
        });
      }
      setAllVariables(prev => {
        const map = new Map(prev.map(v => [v.id, v]));
        list.forEach(v => map.set(v.id, v));
        return Array.from(map.values());
      });
      return list;
    } catch (err) {
      console.error('Erro ao consultar variáveis na API:', err);
      return [];
    }
  }, [variableForm.id, variableForm.branch_ids]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const resetVariableForm = () => {
    setVariableForm({
      id: null,
      name: '',
      description: '',
      formula: '',
      commission_percent: '',
      commission_min_sold_value: '',
      commission_use_goal_value: false,
      show_commission_percentage: true,
      include_in_goal_total: true,
      include_commission_in_goal_total: true,
      available_for_goals: false,
      use_goal_filter_config: false,
      has_accrual_rule: false,
      accrual_rules: [],
      use_sold_qty_for_bonus: false,
      bonus_validation_qty: '',
      variable_bonus_amount: '',
      active: 1,
      branch_ids: [],
      filters: [],
      variable_deps: []
    });
    setShowFormulaDropdown(false);
    setFormulaSearchTerm('');
  };

  // Funções para autocomplete da fórmula
  const handleFormulaChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setVariableForm({ ...variableForm, formula: value });
    setFormulaCursorPos(cursorPos);

    // Verificar se estamos dentro de chaves
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastOpenBrace = textBeforeCursor.lastIndexOf('{');
    const lastCloseBrace = textBeforeCursor.lastIndexOf('}');

    if (lastOpenBrace > lastCloseBrace) {
      // Estamos dentro de chaves, mostrar autocomplete
      const searchTerm = textBeforeCursor.substring(lastOpenBrace + 1);
      setFormulaSearchTerm(searchTerm);
      setShowFormulaDropdown(true);
    } else {
      setShowFormulaDropdown(false);
    }
  };

  const handleFormulaKeyDown = (e) => {
    if (e.key === '{' && !showFormulaDropdown) {
      setShowFormulaDropdown(true);
      setFormulaSearchTerm('');
    } else if (e.key === 'Escape') {
      setShowFormulaDropdown(false);
    }
  };

  const insertPlaceholder = (placeholder) => {
    const input = formulaInputRef.current;
    if (!input) return;

    const value = variableForm.formula;
    const cursorPos = input.selectionStart;

    // Encontrar onde estão as chaves abertas
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastOpenBrace = textBeforeCursor.lastIndexOf('{');

    let newValue;
    let newCursorPos;

    if (lastOpenBrace >= 0 && showFormulaDropdown) {
      // Substituir o conteúdo dentro das chaves
      const beforeBrace = value.substring(0, lastOpenBrace);
      const afterCursor = value.substring(cursorPos);
      newValue = beforeBrace + '{' + placeholder + '}' + afterCursor;
      newCursorPos = lastOpenBrace + placeholder.length + 2; // +2 para as chaves
    } else {
      // Inserir no cursor
      const before = value.substring(0, cursorPos);
      const after = value.substring(cursorPos);
      newValue = before + '{' + placeholder + '}' + after;
      newCursorPos = cursorPos + placeholder.length + 2;
    }

    setVariableForm({ ...variableForm, formula: newValue });
    setShowFormulaDropdown(false);

    // Restaurar foco e posição do cursor
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Obter placeholders disponíveis (filtros + variáveis)
  const getAvailablePlaceholders = () => {
    const filterPlaceholders = variableForm.filters
      .filter(f => f.placeholder && f.filter_id)
      .map(f => ({
        value: f.placeholder,
        label: `${f.placeholder} (${filters.find(fi => fi.id === parseInt(f.filter_id))?.name || 'Filtro não selecionado'})`,
        type: 'filter'
      }));

    const variablePlaceholders = variableForm.variable_deps
      .filter(d => d.placeholder && d.depends_on_variable_id)
      .map(d => ({
        value: d.placeholder,
        label: `${d.placeholder} (${allVariables.find(vi => vi.id === parseInt(d.depends_on_variable_id))?.name || 'Variável não selecionada'})`,
        type: 'variable'
      }));

    const all = [...filterPlaceholders, ...variablePlaceholders];

    if (formulaSearchTerm) {
      return all.filter(p => p.value.toLowerCase().includes(formulaSearchTerm.toLowerCase()));
    }
    return all;
  };

  // Adicionar texto à fórmula
  const appendToFormula = (text) => {
    setVariableForm(prev => ({
      ...prev,
      formula: prev.formula + text
    }));
  };

  // Renderizar preview visual da fórmula
  const renderFormulaPreview = () => {
    if (!variableForm.formula) return null;

    // Regex melhorada para separar todos os elementos: variáveis, operadores, números e espaços
    const parts = variableForm.formula.split(/(\{[^}]+\}|[\+\-\*\/\(\)]|[0-9]+(?:\.[0-9]+)?|\s+)/g).filter(part => part && part.trim() !== '');

    return parts.map((part, idx) => {
      if (part.match(/^\{[^}]+\}$/)) {
        // É um placeholder - mostrar como badge colorido
        const placeholderName = part.slice(1, -1);
        const filterInfo = variableForm.filters.find(f => f.placeholder === placeholderName);
        const varDepInfo = variableForm.variable_deps.find(d => d.placeholder === placeholderName);
        const isVariableDep = !!varDepInfo;
        const filterName = filterInfo ? filters.find(f => f.id === parseInt(filterInfo.filter_id))?.name : null;
        const varDepName = varDepInfo ? allVariables.find(v => v.id === parseInt(varDepInfo.depends_on_variable_id))?.name : null;

        const gradient = isVariableDep
          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        const shadowColor = isVariableDep
          ? 'rgba(16, 185, 129, 0.3)'
          : 'rgba(102, 126, 234, 0.3)';
        const shadowColorHover = isVariableDep
          ? 'rgba(16, 185, 129, 0.4)'
          : 'rgba(102, 126, 234, 0.4)';

        return (
          <span
            key={idx}
            style={{
              background: gradient,
              color: 'white',
              padding: '8px 14px',
              borderRadius: '20px',
              fontWeight: '600',
              fontSize: '0.85rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: `0 2px 8px ${shadowColor}`,
              margin: '2px',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            title={isVariableDep ? (varDepName || 'Variável') : (filterName || 'Filtro')}
            onClick={(e) => handleVariableBadgeClick(e, placeholderName)}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = `0 4px 12px ${shadowColorHover}`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 2px 8px ${shadowColor}`;
            }}
          >
            <FiCode size={14} />
            {placeholderName}
          </span>
        );
      } else if (part.match(/^[\+\-\*\/\(\)]$/)) {
        // É um operador - mostrar destacado
        return (
          <span
            key={idx}
            style={{
              background: '#1e293b',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '12px',
              fontWeight: '700',
              fontSize: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '32px',
              height: '32px',
              margin: '2px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
          >
            {part}
          </span>
        );
      } else if (part.match(/^[0-9]+(?:\.[0-9]+)?$/)) {
        // É um número
        return (
          <span
            key={idx}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '8px 14px',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '0.9rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '2px',
              boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)'
            }}
          >
            {part}
          </span>
        );
      } else {
        // Espaços ou outros caracteres (não deveria ocorrer com a nova regex)
        return null;
      }
    }).filter(Boolean);
  };

  const replicateVariable = (v) => {
    const processedFilters = v.filters?.map((f, idx) => {
      const filterId = String(f.filter_id);
      let placeholder = f.placeholder || '';
      
      if (!placeholder && filterId) {
        const filter = filters.find(fi => fi.id === parseInt(filterId));
        if (filter) {
          placeholder = generatePlaceholderFromFilterName(filter.name);
        }
      }
      
      return { filter_id: filterId, placeholder, sort_order: f.sort_order !== undefined ? Number(f.sort_order) : idx, use_goal_filter_config: Boolean(f.use_goal_filter_config) };
    }) || [];

    const processedVarDeps = v.variable_deps?.map((d, idx) => {
      const varId = String(d.depends_on_variable_id);
      let placeholder = d.placeholder || '';

      if (!placeholder && varId) {
        const vDep = allVariables.find(vi => vi.id === parseInt(varId));
        if (vDep) {
          placeholder = generatePlaceholderFromFilterName(vDep.name);
        }
      }

      return { depends_on_variable_id: varId, placeholder, sort_order: d.sort_order !== undefined ? Number(d.sort_order) : idx };
    }) || [];
    
    setVariableForm({
      id: null,
      name: `${v.name || ''} (Cópia)`,
      description: v.description || '',
      formula: v.formula || '',
      commission_percent: v.commission_percent ?? '',
      commission_min_sold_value: v.commission_min_sold_value ?? '',
      commission_use_goal_value: Boolean(v.commission_use_goal_value),
      show_commission_percentage: v.show_commission_percentage !== undefined ? Boolean(v.show_commission_percentage) : true,
      include_in_goal_total: v.include_in_goal_total !== undefined ? Boolean(v.include_in_goal_total) : true,
      include_commission_in_goal_total: v.include_commission_in_goal_total !== undefined ? Boolean(v.include_commission_in_goal_total) : true,
      available_for_goals: v.available_for_goals !== undefined ? (Number(v.available_for_goals) === 1 || v.available_for_goals === true || v.available_for_goals === '1') : true,
      use_goal_filter_config: Boolean(v.use_goal_filter_config),
      has_accrual_rule: Boolean(v.has_accrual_rule),
      accrual_rules: (v.accrual_rules || []).map(r => ({ ...r })),
      use_sold_qty_for_bonus: Boolean(v.use_sold_qty_for_bonus),
      bonus_validation_qty: v.bonus_validation_qty ?? '',
      variable_bonus_amount: v.variable_bonus_amount ?? '',
      active: v.active ?? 1,
      branch_ids: v.filter_branch ? v.filter_branch.split(',').map(s => s.trim()).filter(Boolean) : [],
      filters: processedFilters,
      variable_deps: processedVarDeps
    });
    setShowVariableModal(true);
  };

  const openNewVariableModal = () => {
    resetVariableForm();
    setShowVariableModal(true);
  };

  const editVariable = (v) => {
    const processedFilters = v.filters?.map((f, idx) => {
      const filterId = String(f.filter_id);
      let placeholder = f.placeholder || '';
      
      if (!placeholder && filterId) {
        const filter = filters.find(fi => fi.id === parseInt(filterId));
        if (filter) {
          placeholder = generatePlaceholderFromFilterName(filter.name);
        }
      }
      
      return { filter_id: filterId, placeholder, sort_order: f.sort_order !== undefined ? Number(f.sort_order) : idx, use_goal_filter_config: Boolean(f.use_goal_filter_config) };
    }) || [];

    const processedVarDeps = v.variable_deps?.map((d, idx) => {
      const varId = String(d.depends_on_variable_id);
      let placeholder = d.placeholder || '';

      if (!placeholder && varId) {
        const vDep = allVariables.find(vi => vi.id === parseInt(varId));
        if (vDep) {
          placeholder = generatePlaceholderFromFilterName(vDep.name);
        }
      }

      return { depends_on_variable_id: varId, placeholder, sort_order: d.sort_order !== undefined ? Number(d.sort_order) : idx };
    }) || [];
    
    setVariableForm({
      id: v.id,
      name: v.name || '',
      description: v.description || '',
      formula: v.formula || '',
      commission_percent: v.commission_percent ?? '',
      commission_min_sold_value: v.commission_min_sold_value ?? '',
      commission_use_goal_value: Boolean(v.commission_use_goal_value),
      show_commission_percentage: v.show_commission_percentage !== undefined ? Boolean(v.show_commission_percentage) : true,
      include_in_goal_total: v.include_in_goal_total !== undefined ? Boolean(v.include_in_goal_total) : true,
      include_commission_in_goal_total: v.include_commission_in_goal_total !== undefined ? Boolean(v.include_commission_in_goal_total) : true,
      available_for_goals: v.available_for_goals !== undefined ? (Number(v.available_for_goals) === 1 || v.available_for_goals === true || v.available_for_goals === '1') : true,
      use_goal_filter_config: Boolean(v.use_goal_filter_config),
      has_accrual_rule: Boolean(v.has_accrual_rule),
      accrual_rules: (v.accrual_rules || []).map(r => ({ ...r })),
      use_sold_qty_for_bonus: Boolean(v.use_sold_qty_for_bonus),
      bonus_validation_qty: v.bonus_validation_qty ?? '',
      variable_bonus_amount: v.variable_bonus_amount ?? '',
      active: v.active ?? 1,
      branch_ids: v.filter_branch ? v.filter_branch.split(',').map(s => s.trim()).filter(Boolean) : [],
      filters: processedFilters,
      variable_deps: processedVarDeps
    });
    setShowVariableModal(true);
  };

  const saveVariable = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const payload = {
        name: variableForm.name,
        description: variableForm.description,
        formula: variableForm.formula,
        commission_percent: variableForm.commission_percent === '' ? 0 : parseFloat(variableForm.commission_percent),
        show_commission_percentage: Boolean(variableForm.show_commission_percentage),
        commission_min_sold_value: variableForm.commission_min_sold_value === '' ? 0 : parseFloat(variableForm.commission_min_sold_value),
        commission_use_goal_value: Boolean(variableForm.commission_use_goal_value),
        include_in_goal_total: Boolean(variableForm.include_in_goal_total),
        include_commission_in_goal_total: Boolean(variableForm.include_commission_in_goal_total),
        available_for_goals: Boolean(variableForm.available_for_goals),
        use_goal_filter_config: Boolean(variableForm.use_goal_filter_config),
        has_accrual_rule: Boolean(variableForm.has_accrual_rule),
        accrual_rules: variableForm.accrual_rules
          .filter(r => r.reference_type && r.reference_id)
          .map(r => ({
            reference_type: r.reference_type,
            reference_id: parseInt(r.reference_id, 10),
            threshold: parseFloat(r.threshold) || 0,
            threshold_quantity: parseInt(r.threshold_quantity) || null,
            accrual_percent: parseFloat(r.accrual_percent) || 0,
            accrual_amount: parseFloat(r.accrual_amount) || 0,
            filter_start_date: r.filter_start_date || null,
            filter_end_date: r.filter_end_date || null,
            use_goal_value: r.use_goal_value ? 1 : 0
          })),
        use_sold_qty_for_bonus: Boolean(variableForm.use_sold_qty_for_bonus),
        bonus_validation_qty: variableForm.bonus_validation_qty === '' ? null : parseFloat(variableForm.bonus_validation_qty),
        variable_bonus_amount: variableForm.variable_bonus_amount === '' ? null : parseFloat(variableForm.variable_bonus_amount),
        active: variableForm.active,
        filter_branch: variableForm.branch_ids.length > 0 ? variableForm.branch_ids.join(',') : null,
        filters: variableForm.filters
          .filter(f => f.filter_id && f.placeholder)
          .map((f, idx) => ({
            filter_id: parseInt(f.filter_id, 10),
            placeholder: f.placeholder,
            sort_order: idx,
            use_goal_filter_config: Boolean(f.use_goal_filter_config)
          })),
        variable_deps: variableForm.variable_deps
          .filter(d => d.depends_on_variable_id && d.placeholder)
          .map((d, idx) => ({
            depends_on_variable_id: parseInt(d.depends_on_variable_id, 10),
            placeholder: d.placeholder,
            sort_order: idx
          }))
      };

      if (variableForm.id) {
        await faturamentoService.updateVariable(variableForm.id, payload);
        setMessage({ type: 'success', text: 'Variável atualizada.' });
      } else {
        await faturamentoService.createVariable(payload);
        setMessage({ type: 'success', text: 'Variável criada.' });
      }

      resetVariableForm();
      setShowVariableModal(false);
      setPage(1);
      await loadVariables({ page: 1, per_page: perPage });
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Erro ao salvar';
      if (showVariableModal) {
        setErrorModalTitle('Erro ao Salvar');
        setErrorMessage(errorMsg);
        setShowErrorModal(true);
      } else {
        setMessage({ type: 'danger', text: errorMsg });
      }
    } finally {
      setLoading(false);
    }
  };

  const removeVariable = async (id) => {
    const variable = variables.find(v => v.id === id);
    setVariableToDelete(variable);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!variableToDelete) return;
    
    setDeleting(true);
    try {
      await faturamentoService.deleteVariable(variableToDelete.id);
      setMessage({ type: 'success', text: `Variável "${variableToDelete.name}" removida com sucesso!` });
      setShowDeleteModal(false);
      setVariableToDelete(null);
      if (variableForm.id === variableToDelete.id) {
        resetVariableForm();
      }
      setPage(1);
      loadVariables({ page: 1, per_page: perPage });
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Erro ao remover variável.';
      setErrorModalTitle('Erro ao Remover');
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedVarIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (filteredItems) => {
    if (selectedVarIds.length === filteredItems.length && filteredItems.length > 0) {
      setSelectedVarIds([]);
    } else {
      setSelectedVarIds(filteredItems.map((v) => v.id));
    }
  };

  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const id of selectedVarIds) {
        try {
          await faturamentoService.deleteVariable(id);
          successCount++;
        } catch (e) {
          console.error(`Erro ao excluir variável ${id}:`, e);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        setMessage({ type: 'success', text: `${successCount} variáveis excluídas com sucesso.` });
      } else {
        setMessage({ type: 'warning', text: `${successCount} variáveis excluídas, ${errorCount} falharam.` });
      }
      
      setShowBulkDeleteModal(false);
      setSelectedVarIds([]);
      setPage(1);
      loadVariables({ page: 1, per_page: perPage });
    } catch (e) {
      setMessage({ type: 'danger', text: 'Erro ao excluir lote de variáveis.' });
    } finally {
      setBulkDeleting(false);
    }
  };

  const filteredVariables = variables.filter(v => {
    const matchesBranch = filterBranchSearch.length === 0 ||
      filterBranchSearch.some(selectedCode =>
        (v.filter_branch || '').split(',').some(code =>
          code.trim() === selectedCode
        )
      );
    return matchesBranch;
  });

  const addFilterToVariable = () => {
    setVariableForm(prev => ({
      ...prev,
      filters: [...prev.filters, {
        filter_id: '',
        placeholder: '',
        sort_order: prev.filters.length,
        use_goal_filter_config: Boolean(prev.use_goal_filter_config)
      }]
    }));
  };

  const addVariableDep = () => {
    setVariableForm(prev => ({
      ...prev,
      variable_deps: [...prev.variable_deps, { depends_on_variable_id: '', placeholder: '', sort_order: prev.variable_deps.length }]
    }));
  };

  const moveFilterInVariable = (index, direction) => {
    setVariableForm(prev => {
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.filters.length) return prev;
      const newFilters = [...prev.filters];
      const temp = newFilters[index];
      newFilters[index] = newFilters[targetIndex];
      newFilters[targetIndex] = temp;
      return { ...prev, filters: newFilters };
    });
  };

  const moveVariableDepInVariable = (index, direction) => {
    setVariableForm(prev => {
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= prev.variable_deps.length) return prev;
      const newDeps = [...prev.variable_deps];
      const temp = newDeps[index];
      newDeps[index] = newDeps[targetIndex];
      newDeps[targetIndex] = temp;
      return { ...prev, variable_deps: newDeps };
    });
  };

  const updateVariableDep = (index, field, value, selectedOpt = null) => {
    setVariableForm(prev => {
      const newDeps = [...prev.variable_deps];
      const oldDep = newDeps[index];
      const oldPlaceholder = oldDep.placeholder;

      newDeps[index] = { ...newDeps[index], [field]: value };

      if (field === 'depends_on_variable_id') {
        if (value) {
          const selectedVar = selectedOpt || allVariables.find(v => v.id === parseInt(value));
          if (selectedVar) {
            const autoPlaceholder = generatePlaceholderFromFilterName(selectedVar.name);
            newDeps[index].placeholder = autoPlaceholder;
            if (oldPlaceholder) {
              setTimeout(() => {
                updatePlaceholdersInFormula(oldPlaceholder, autoPlaceholder);
              }, 0);
            }
          }
        } else {
          newDeps[index].placeholder = '';
        }
      } else if (field === 'placeholder') {
        if (oldPlaceholder && value) {
          setTimeout(() => {
            updatePlaceholdersInFormula(oldPlaceholder, value);
          }, 0);
        }
      }

      return { ...prev, variable_deps: newDeps };
    });

    if (selectedOpt && selectedOpt.id) {
      setAllVariables(prev => {
        if (prev.some(v => v.id === selectedOpt.id)) return prev;
        return [...prev, selectedOpt];
      });
    }
  };

  const removeVariableDep = (index) => {
    setVariableForm(prev => {
      const newDeps = prev.variable_deps.filter((_, i) => i !== index);
      return { ...prev, variable_deps: newDeps };
    });
  };

  // Gerar placeholder automático a partir do nome do filtro
  const generatePlaceholderFromFilterName = (filterName) => {
    if (!filterName) return '';
    
    // Converter para minúsculas, remover caracteres especiais e espaços, substituir por underscore
    return filterName
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .replace(/[^a-z0-9\s]/g, '') // Manter apenas letras, números e espaços
      .replace(/\s+/g, '_') // Substituir espaços por underscore
      .replace(/_+/g, '_') // Remover underscores múltiplos
      .replace(/^_|_$/g, ''); // Remover underscores no início ou fim
  };

  // Função para atualizar placeholders na fórmula quando um placeholder é alterado
  const updatePlaceholdersInFormula = (oldPlaceholder, newPlaceholder) => {
    if (!oldPlaceholder || !newPlaceholder || oldPlaceholder === newPlaceholder) {
      return;
    }
    
    setVariableForm(prev => {
      // Substituir todas as ocorrências do placeholder antigo pelo novo na fórmula
      const updatedFormula = prev.formula.replace(
        new RegExp(`\\{${oldPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}`, 'g'),
        `{${newPlaceholder}}`
      );
      
      return { ...prev, formula: updatedFormula };
    });
  };

  // Função para substituir uma variável específica na fórmula
  const replaceVariableInFormula = (oldVariable, newVariable) => {
    if (!oldVariable || !newVariable || oldVariable === newVariable) {
      return;
    }
    
    setVariableForm(prev => {
      // Substituir apenas a ocorrência específica da variável
      const updatedFormula = prev.formula.replace(
        new RegExp(`\\{${oldVariable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}`, 'g'),
        `{${newVariable}}`
      );
      
      return { ...prev, formula: updatedFormula };
    });
    
    // Fechar dropdown após substituição
    setShowVariableReplaceDropdown(false);
    setSelectedVariableToReplace(null);
  };

  // Função para abrir dropdown de substituição de variável
  const handleVariableBadgeClick = (event, variableName) => {
    event.preventDefault();
    event.stopPropagation();
    
    const rect = event.target.getBoundingClientRect();
    setVariableReplacePosition({
      x: rect.left,
      y: rect.bottom + 5
    });
    setSelectedVariableToReplace(variableName);
    setShowVariableReplaceDropdown(true);
  };

  const updateFilterInVariable = (index, field, value, selectedOpt = null) => {
    setVariableForm(prev => {
      const newFilters = [...prev.filters];
      const oldFilter = newFilters[index];
      const oldPlaceholder = oldFilter.placeholder;
      
      newFilters[index] = { ...newFilters[index], [field]: value };
      
      // Se o campo alterado for filter_id, sempre atualizar o placeholder
      if (field === 'filter_id') {
        if (value) {
          // Se um filtro foi selecionado, gerar/atualizar placeholder automaticamente
          const selectedFilter = selectedOpt || filters.find(f => f.id === parseInt(value));
          if (selectedFilter) {
            const autoPlaceholder = generatePlaceholderFromFilterName(selectedFilter.name);
            newFilters[index].placeholder = autoPlaceholder;
            
            // Atualizar placeholders na fórmula se o placeholder antigo existia
            if (oldPlaceholder) {
              setTimeout(() => {
                updatePlaceholdersInFormula(oldPlaceholder, autoPlaceholder);
              }, 0);
            }
          }
        } else {
          // Se nenhum filtro foi selecionado, limpar o placeholder
          newFilters[index].placeholder = '';
        }
      } else if (field === 'placeholder') {
        // Se o placeholder foi alterado manualmente, atualizar na fórmula
        if (oldPlaceholder && value) {
          setTimeout(() => {
            updatePlaceholdersInFormula(oldPlaceholder, value);
          }, 0);
        }
      }
      
      return { ...prev, filters: newFilters };
    });

    if (selectedOpt && selectedOpt.id) {
      setFilters(prev => {
        if (prev.some(f => f.id === selectedOpt.id)) return prev;
        return [...prev, selectedOpt];
      });
    }
  };

  const removeFilterFromVariable = (index) => {
    setVariableForm(prev => {
      const newFilters = prev.filters.filter((_, i) => i !== index);
      return { ...prev, filters: newFilters };
    });
  };

  return (
    <div className="container-fluid py-4">
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h2 className="mb-1">Variáveis de Cálculo</h2>
          <p className="text-muted mb-0">
            Fórmulas customizadas que utilizam resultados de filtros para cálculos automáticos.
          </p>
        </div>
        <button type="button" className="btn-modern btn-modern-light" onClick={() => loadVariables()} disabled={loading}>
          <FiRefreshCw size={16} className={loading ? 'spin-ico' : ''} />
        </button>
      </div>

      {message && <div className={`alert alert-${message.type} py-2 mb-4`}>{message.text}</div>}

      {/* Botao Nova Variável */}
      {canManage && (
        <div className="mb-4">
          <button type="button" className="btn-modern btn-modern-primary" onClick={openNewVariableModal} style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1' }}>
            <FiPlus size={16} className="me-2" />Nova Variável
          </button>
        </div>
      )}

      {/* Listagem de Variáveis */}
      <div className="settings-container">
        <div className="card-modern shadow-sm-modern">
          <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h5 className="mb-0 fw-bold text-dark">Variáveis de Cálculo</h5>
              <p className="text-muted small mb-0">Fórmulas customizadas usando resultados de filtros</p>
            </div>
            
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div style={{ position: 'relative', width: '250px' }}>
                <FiSearch
                  size={16}
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', zIndex: 1 }}
                />
                <input
                  type="text"
                  className="form-control-modern"
                  placeholder="Buscar variável..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  style={{ paddingLeft: '38px', height: '40px' }}
                />
              </div>

              <div style={{ width: '220px' }}>
                <MultiSelectSearch
                  label=""
                  placeholder="Filtrar filial..."
                  value={filterBranchSearch}
                  onChange={(val) => {
                    setFilterBranchSearch(val);
                    setPage(1);
                  }}
                  options={branches}
                  badgeColor="#10b981"
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.codigo}
                />
              </div>

              {filterBranchSearch.length > 0 && (
                <button
                  type="button"
                  className="btn-modern btn-modern-light btn-modern-sm"
                  onClick={() => {
                    setFilterBranchSearch([]);
                    setPage(1);
                  }}
                  title="Limpar filtros"
                >
                  <FiX size={14} />
                </button>
              )}

              {canManage && selectedVarIds.length > 0 && (
                <button
                  type="button"
                  className="btn-modern btn-modern-light-danger btn-modern-sm animate__animated animate__fadeIn"
                  onClick={() => setShowBulkDeleteModal(true)}
                >
                  <FiTrash2 size={14} className="me-1" />
                  Excluir ({selectedVarIds.length})
                </button>
              )}
            </div>
          </div>
          <div className="card-body-modern p-4 pt-0" style={{ position: 'relative' }}>
            {loading && filteredVariables.length > 0 && (
              <Loading.Overlay text="Atualizando variáveis..." size="sm" />
            )}
            <div className="table-responsive">
              <table className="table-modern w-100">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={filteredVariables.length > 0 && selectedVarIds.length === filteredVariables.length}
                        onChange={() => toggleSelectAll(filteredVariables)}
                      />
                    </th>
                    <th style={{ width: '22%', cursor: 'pointer' }} onClick={() => handleSort('name')}>Nome{renderSortIcon('name')}</th>
                    <th style={{ width: '35%', cursor: 'pointer' }} onClick={() => handleSort('formula')}>Fórmula{renderSortIcon('formula')}</th>
                    <th style={{ width: '15%', cursor: 'pointer' }} onClick={() => handleSort('filter_count')}>Filtros{renderSortIcon('filter_count')}</th>
                    <th style={{ width: '13%', cursor: 'pointer' }} onClick={() => handleSort('filter_branch')}>Filiais{renderSortIcon('filter_branch')}</th>
                    <th style={{ width: '15%' }} className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && filteredVariables.length === 0 ? (
                    <Loading.Table
                      colSpan={6}
                      text="Carregando variáveis de cálculo..."
                      subtext="Buscando fórmulas, filtros vinculados e regras de comissão..."
                    />
                  ) : filteredVariables.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">Nenhuma variável encontrada.</td>
                    </tr>
                  ) : (
                    filteredVariables.map((v) => (
                      <tr key={v.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedVarIds.includes(v.id)}
                            onChange={() => toggleSelectOne(v.id)}
                          />
                        </td>
                        <td className="fw-semibold text-dark">
                          {v.name}
                          {v.active === 0 && <span className="badge bg-secondary ms-2">Inativo</span>}
                        </td>
                        <td className="text-muted font-monospace" style={{ fontSize: '0.85rem' }}>{v.formula}</td>
                        <td className="text-muted">
                          {v.filters?.length > 0 || v.variable_deps?.length > 0
                            ? `${v.filters?.length || 0} filtro(s)${v.variable_deps?.length > 0 ? `, ${v.variable_deps.length} var(s)` : ''}`
                            : '-'}
                          {(v.use_goal_filter_config || v.filters?.some(f => Number(f.use_goal_filter_config) === 1)) && (
                            <span
                              className="badge ms-1"
                              style={{
                                fontSize: '0.65rem',
                                backgroundColor: '#d1fae5',
                                color: '#065f46',
                                border: '1px solid #a7f3d0'
                              }}
                              title="Possui filtros configurados para usar valores exclusivos da meta"
                            >
                              ⚡ Meta Exclusiva
                            </span>
                          )}
                        </td>
                        <td className="text-muted" style={{ fontSize: '0.82rem' }}>
                          {v.filter_branch ? v.filter_branch.split(',').map(s => s.trim()).filter(Boolean).map((code, i) => (
                            <span key={i} style={{
                              display: 'inline-block',
                              padding: '2px 7px',
                              margin: '1px 2px',
                              borderRadius: '4px',
                              background: '#f1f5f9',
                              border: '1px solid #e2e8f0',
                              fontSize: '0.72rem',
                              whiteSpace: 'nowrap'
                            }}>{code}</span>
                          )) : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Todas</span>}
                        </td>
                        <td>
                          <div className="d-flex justify-content-end align-items-center gap-2">
                            {canManage ? (
                              <>
                                <button
                                  type="button"
                                  className="btn-modern btn-modern-light btn-modern-sm"
                                  onClick={() => replicateVariable(v)}
                                  title="Replicar variável"
                                  style={{ padding: '6px 10px' }}
                                >
                                  <FiCopy size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="btn-modern btn-modern-light btn-modern-sm"
                                  onClick={() => editVariable(v)}
                                  title="Editar variável"
                                  style={{ padding: '6px 10px' }}
                                >
                                  <FiEdit2 size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="btn-modern btn-modern-light-danger btn-modern-sm"
                                  onClick={() => removeVariable(v.id)}
                                  title="Excluir variável"
                                  style={{ padding: '6px 10px' }}
                                >
                                  <FiTrash2 size={14} />
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                className="btn-modern btn-modern-light btn-modern-sm"
                                onClick={() => editVariable(v)}
                                title="Visualizar variável"
                                style={{ padding: '6px 10px' }}
                              >
                                <FiSearch size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            <BIPagination
              page={page}
              perPage={perPage}
              total={totalRecords}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
              itemLabel="variáveis"
              perPageOptions={[10, 25, 50, 100, 200]}
            />
          </div>
        </div>
      </div>

      {/* Modal Nova/Editar Variável */}
      {showVariableModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
        >
          <div
            style={{
              background: 'white', borderRadius: '20px', width: '100%',
              maxWidth: '700px', maxHeight: '90vh',
              boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
              animation: 'modalSlideIn 0.25s ease',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '24px 28px 20px',
              borderBottom: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'white', zIndex: 10, flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <FiCode size={20} />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700, fontSize: '1rem' }}>
                    {variableForm.id ? 'Editar Variável' : 'Nova Variável'}
                  </h5>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>
                    {variableForm.id ? 'Atualize a variável de cálculo' : 'Crie uma variável usando fórmulas e filtros'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setShowVariableModal(false); resetVariableForm(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '8px' }}
              >
                <FiX size={20} color="#64748b" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={saveVariable} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
                <div className="form-group-modern mb-3">
                  <label>Nome da Variável</label>
                  <input
                    type="text"
                    className="form-control-modern"
                    value={variableForm.name}
                    onChange={(e) => setVariableForm({ ...variableForm, name: e.target.value })}
                    placeholder="Ex: Total de Vendas com Desconto"
                    required
                  />
                </div>

                <div className="form-group-modern mb-3">
                  <label>Descrição</label>
                  <textarea
                    className="form-control-modern"
                    value={variableForm.description}
                    onChange={(e) => setVariableForm({ ...variableForm, description: e.target.value })}
                    placeholder="Descrição opcional da variável"
                    rows={2}
                  />
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <div className="form-group-modern mb-0">
                      <label>Adicional (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control-modern"
                        value={variableForm.commission_percent}
                        onChange={(e) => setVariableForm({ ...variableForm, commission_percent: e.target.value })}
                        placeholder="Ex: 1.50"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group-modern mb-0">
                      <label>Valor mínimo (R$)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control-modern"
                        value={variableForm.commission_use_goal_value ? '' : variableForm.commission_min_sold_value}
                        onChange={(e) => setVariableForm({ ...variableForm, commission_min_sold_value: e.target.value })}
                        placeholder={variableForm.commission_use_goal_value ? "Usando Meta" : "Ex: 500.00"}
                        disabled={variableForm.commission_use_goal_value}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group-modern mb-3">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="commission_use_goal_value_var"
                          className="form-check-input"
                          checked={variableForm.commission_use_goal_value}
                          onChange={(e) => setVariableForm({ ...variableForm, commission_use_goal_value: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label className="form-check-label" htmlFor="commission_use_goal_value_var" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                          Usar Valor da Meta como limite mínimo
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="show_commission_percentage_var"
                          className="form-check-input"
                          checked={variableForm.show_commission_percentage}
                          onChange={(e) => setVariableForm({ ...variableForm, show_commission_percentage: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label className="form-check-label" htmlFor="show_commission_percentage_var" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                          Exibir porcentagem no relatório
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="include_in_goal_total_var"
                          className="form-check-input"
                          checked={variableForm.include_in_goal_total}
                          onChange={(e) => setVariableForm({ ...variableForm, include_in_goal_total: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label className="form-check-label" htmlFor="include_in_goal_total_var" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                          Incluir no total da meta
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="include_commission_in_goal_total_var"
                          className="form-check-input"
                          checked={variableForm.include_commission_in_goal_total}
                          onChange={(e) => setVariableForm({ ...variableForm, include_commission_in_goal_total: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label className="form-check-label" htmlFor="include_commission_in_goal_total_var" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                          Incluir adicional na meta
                        </label>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="available_for_goals_var"
                          className="form-check-input"
                          checked={variableForm.available_for_goals}
                          onChange={(e) => setVariableForm({ ...variableForm, available_for_goals: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label className="form-check-label" htmlFor="available_for_goals_var" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                          <strong>Disponível para Metas</strong> (permite selecionar esta variável no cadastro e edição de metas)
                        </label>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="use_goal_filter_config_var"
                          className="form-check-input"
                          checked={variableForm.use_goal_filter_config}
                          onChange={(e) => setVariableForm({ ...variableForm, use_goal_filter_config: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label className="form-check-label" htmlFor="use_goal_filter_config_var" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                          <strong>Usar regras exclusivas da meta nos filtros por padrão</strong> (prioriza regras/metas customizadas na Meta do vendedor para os filtros desta variável)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Regras de Acréscimo */}
                <div className="form-group-modern mb-3" style={{
                  background: variableForm.has_accrual_rule ? '#fffbeb' : 'transparent',
                  border: variableForm.has_accrual_rule ? '1px solid #fbbf24' : '1px solid transparent',
                  borderRadius: '12px', padding: '16px', transition: 'all 0.2s'
                }}>
                  <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: variableForm.has_accrual_rule ? '12px' : '0' }}>
                    <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id="has_accrual_rule_var"
                        className="form-check-input"
                        checked={variableForm.has_accrual_rule}
                        onChange={(e) => setVariableForm({ ...variableForm, has_accrual_rule: e.target.checked })}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <label className="form-check-label" htmlFor="has_accrual_rule_var" style={{ cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#92400e' }}>
                        Regras de Acréscimo de Adicional
                      </label>
                    </div>
                    {variableForm.has_accrual_rule && (
                      <button
                        type="button"
                        className="btn-modern btn-modern-sm btn-modern-warning"
                        onClick={() => setVariableForm(prev => ({
                          ...prev,
                          accrual_rules: [...prev.accrual_rules, { reference_type: 'filter', reference_id: '', threshold: '', threshold_quantity: '', accrual_percent: '', accrual_amount: '' }]
                        }))}
                      >
                        <FiPlus size={14} /> Adicionar Regra
                      </button>
                    )}
                  </div>

                  {variableForm.has_accrual_rule && (
                    <div style={{ paddingLeft: '26px' }}>
                      <small className="text-muted d-block mb-2" style={{ fontSize: '0.75rem' }}>
                        Cada regra é avaliada independentemente. Se o valor (R$) E a quantidade de unidades atingirem os thresholds definidos, o acréscimo em percentual (%) e/ou valor fixo (R$) é somado ao adicional.
                      </small>

                      {variableForm.accrual_rules.length === 0 && (
                        <div className="text-muted text-center py-2" style={{ background: '#f8fafc', borderRadius: '8px', fontSize: '0.85rem' }}>
                          Nenhuma regra de acréscimo adicionada.
                        </div>
                      )}

                      {variableForm.accrual_rules.map((rule, idx) => (
                        <div key={idx} style={{
                          background: '#fefce8', padding: '10px 12px', borderRadius: '8px',
                          borderLeft: '4px solid #f59e0b', marginBottom: '8px'
                        }}>
                          <div className="d-flex gap-2 align-items-end" style={{ flexWrap: 'wrap' }}>
                            <div style={{ minWidth: '120px' }}>
                              <label className="small text-muted">Tipo</label>
                              <select
                                className="form-control-modern form-control-sm"
                                value={rule.reference_type}
                                onChange={(e) => {
                                  const newRules = [...variableForm.accrual_rules];
                                  const newType = e.target.value;
                                  const now = new Date();
                                  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                                  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                                  const fmt = (d) => d.toISOString().slice(0, 10);
                                  newRules[idx] = {
                                    ...newRules[idx],
                                    reference_type: newType,
                                    reference_id: '',
                                    filter_start_date: newType === 'report' ? fmt(firstDay) : undefined,
                                    filter_end_date: newType === 'report' ? fmt(lastDay) : undefined,
                                    use_goal_value: undefined,
                                  };
                                  setVariableForm({ ...variableForm, accrual_rules: newRules });
                                }}
                              >
                                <option value="filter">Filtro</option>
                                <option value="variable">Variável</option>
                                <option value="report">Relatório</option>
                              </select>
                            </div>
                            <div style={{ minWidth: '150px', flex: 1 }}>
                              <label className="small text-muted">
                                {rule.reference_type === 'variable' ? 'Variável' : rule.reference_type === 'report' ? 'Relatório' : 'Filtro'}
                              </label>
                              <select
                                className="form-control-modern form-control-sm"
                                value={rule.reference_id}
                                onChange={(e) => {
                                  const newRules = [...variableForm.accrual_rules];
                                  newRules[idx] = { ...newRules[idx], reference_id: e.target.value };
                                  setVariableForm({ ...variableForm, accrual_rules: newRules });
                                }}
                              >
                                <option value="">Selecione...</option>
                                {rule.reference_type === 'filter'
                                  ? filters.map(f => <option key={f.id} value={f.id}>{f.name}</option>)
                                    : rule.reference_type === 'report'
                                    ? (() => {
                                        const filtered = reports.filter(r => {
                                          if (variableForm.branch_ids.length > 0) {
                                            const reportBranch = String(r.vendor_codfilial || '');
                                            if (!reportBranch || !variableForm.branch_ids.includes(reportBranch)) return false;
                                          }
                                          if (!rule.filter_start_date || !rule.filter_end_date) return true;
                                          return r.period_start <= rule.filter_end_date && r.period_end >= rule.filter_start_date;
                                        });
                                        return filtered.length > 0
                                          ? filtered.map(r => (
                                              <option key={r.id} value={r.id}>
                                                {r.name || `Relatório #${r.id}`}
                                              </option>
                                            ))
                                          : <option value="" disabled>Nenhum relatório no período</option>;
                                      })()
                                    : allVariables
                                        .filter(v => v.id !== variableForm.id)
                                        .filter(v => {
                                          if (variableForm.branch_ids.length === 0) return true;
                                          const vBranches = (v.filter_branch || '').split(',').map(s => s.trim()).filter(Boolean);
                                          return vBranches.length === 0 || vBranches.some(code => variableForm.branch_ids.includes(code));
                                        })
                                        .map(v => <option key={v.id} value={v.id}>{v.name}</option>)
                                }
                              </select>
                            </div>
                            <div style={{ minWidth: '110px' }}>
                              <label className="small text-muted">Meta (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control-modern form-control-sm"
                                value={rule.use_goal_value ? '' : rule.threshold}
                                onChange={(e) => {
                                  const newRules = [...variableForm.accrual_rules];
                                  newRules[idx] = { ...newRules[idx], threshold: e.target.value };
                                  setVariableForm({ ...variableForm, accrual_rules: newRules });
                                }}
                                placeholder={rule.use_goal_value ? 'Usando meta' : '10000'}
                                disabled={rule.use_goal_value}
                              />
                            </div>
                            <div style={{ minWidth: '90px' }}>
                              <label className="small text-muted">Qtd Unid.</label>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                className="form-control-modern form-control-sm"
                                value={rule.threshold_quantity || ''}
                                onChange={(e) => {
                                  const newRules = [...variableForm.accrual_rules];
                                  newRules[idx] = { ...newRules[idx], threshold_quantity: e.target.value };
                                  setVariableForm({ ...variableForm, accrual_rules: newRules });
                                }}
                                placeholder="Ex: 100"
                              />
                              {rule.reference_type === 'report' && (
                                <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                                  <input
                                    type="checkbox"
                                    id={`use_goal_${idx}`}
                                    className="form-check-input"
                                    checked={Boolean(rule.use_goal_value)}
                                    onChange={(e) => {
                                      const newRules = [...variableForm.accrual_rules];
                                      newRules[idx] = { ...newRules[idx], use_goal_value: e.target.checked ? 1 : 0 };
                                      setVariableForm({ ...variableForm, accrual_rules: newRules });
                                    }}
                                    style={{ width: '14px', height: '14px', cursor: 'pointer', margin: 0 }}
                                  />
                                  <label htmlFor={`use_goal_${idx}`} style={{ cursor: 'pointer', fontSize: '0.7rem', color: '#64748b', margin: 0, userSelect: 'none' }}>
                                    Usar valor da meta cadastrada
                                  </label>
                                </div>
                              )}
                            </div>
                            <div style={{ minWidth: '100px' }}>
                              <label className="small text-muted">% Acréscimo</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control-modern form-control-sm"
                                value={rule.accrual_percent}
                                onChange={(e) => {
                                  const newRules = [...variableForm.accrual_rules];
                                  newRules[idx] = { ...newRules[idx], accrual_percent: e.target.value };
                                  setVariableForm({ ...variableForm, accrual_rules: newRules });
                                }}
                                placeholder="0.50"
                              />
                            </div>
                            <div style={{ minWidth: '100px' }}>
                              <label className="small text-muted">R$ Acréscimo</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control-modern form-control-sm"
                                value={rule.accrual_amount || ''}
                                onChange={(e) => {
                                  const newRules = [...variableForm.accrual_rules];
                                  newRules[idx] = { ...newRules[idx], accrual_amount: e.target.value };
                                  setVariableForm({ ...variableForm, accrual_rules: newRules });
                                }}
                                placeholder="0.00"
                              />
                            </div>
                            <button
                              type="button"
                              className="btn-modern btn-modern-light-danger btn-modern-sm"
                              onClick={() => setVariableForm(prev => ({
                                ...prev,
                                accrual_rules: prev.accrual_rules.filter((_, i) => i !== idx)
                              }))}
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                          {rule.reference_type !== 'variable' && (
                            <div className="d-flex gap-2 align-items-end" style={{ flexWrap: 'wrap', marginTop: '6px' }}>
                              <div style={{ minWidth: '140px' }}>
                                <label className="small text-muted">Filtrar período - Início</label>
                                <input
                                  type="date"
                                  className="form-control-modern form-control-sm"
                                  value={rule.filter_start_date || ''}
                                  onChange={(e) => {
                                    const newRules = [...variableForm.accrual_rules];
                                    newRules[idx] = { ...newRules[idx], filter_start_date: e.target.value };
                                    setVariableForm({ ...variableForm, accrual_rules: newRules });
                                  }}
                                />
                              </div>
                              <div style={{ minWidth: '140px' }}>
                                <label className="small text-muted">Fim</label>
                                <input
                                  type="date"
                                  className="form-control-modern form-control-sm"
                                  value={rule.filter_end_date || ''}
                                  onChange={(e) => {
                                    const newRules = [...variableForm.accrual_rules];
                                    newRules[idx] = { ...newRules[idx], filter_end_date: e.target.value };
                                    setVariableForm({ ...variableForm, accrual_rules: newRules });
                                  }}
                                />
                              </div>
                              <div style={{ minWidth: '160px' }}>
                                <label className="small text-muted">Mês</label>
                                <input
                                  type="month"
                                  className="form-control-modern form-control-sm"
                                  value=""
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (!val) return;
                                    const [year, month] = val.split('-').map(Number);
                                    const pad = (n) => String(n).padStart(2, '0');
                                    const end = new Date(year, month, 0);
                                    const newRules = [...variableForm.accrual_rules];
                                    newRules[idx] = {
                                      ...newRules[idx],
                                      filter_start_date: `${year}-${pad(month)}-01`,
                                      filter_end_date: `${year}-${pad(month)}-${pad(end.getDate())}`,
                                    };
                                    setVariableForm({ ...variableForm, accrual_rules: newRules });
                                  }}
                                  title="Selecionar mês"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bonificação por Quantidade Vendida */}
                <div className="form-group-modern mb-3" style={{
                  background: variableForm.use_sold_qty_for_bonus ? '#f0fdf4' : 'transparent',
                  border: variableForm.use_sold_qty_for_bonus ? '1px solid #86efac' : '1px solid transparent',
                  borderRadius: '12px', padding: '16px', transition: 'all 0.2s'
                }}>
                  <div className="form-check-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: variableForm.use_sold_qty_for_bonus ? '12px' : '0' }}>
                    <input
                      type="checkbox"
                      id="use_sold_qty_for_bonus_var"
                      className="form-check-input"
                      checked={variableForm.use_sold_qty_for_bonus}
                      onChange={(e) => setVariableForm({ ...variableForm, use_sold_qty_for_bonus: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label className="form-check-label" htmlFor="use_sold_qty_for_bonus_var" style={{ cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#166534' }}>
                      Somar quantidade de itens vendidos para validar adição de bonificação
                    </label>
                  </div>

                  {variableForm.use_sold_qty_for_bonus && (
                    <div style={{ paddingLeft: '26px' }}>
                      <small className="text-muted d-block mb-2" style={{ fontSize: '0.75rem' }}>
                        A soma da quantidade de itens vendidos de todos os filtros vinculados será comparada com o valor informado. Se atingir a quantidade mínima, o valor da bonificação será adicionado ao adicional.
                      </small>
                      <div className="d-flex gap-3 align-items-end" style={{ flexWrap: 'wrap' }}>
                        <div style={{ minWidth: '150px' }}>
                          <label className="small text-muted">Quantidade Mínima</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="form-control-modern form-control-sm"
                            value={variableForm.bonus_validation_qty}
                            onChange={(e) => setVariableForm({ ...variableForm, bonus_validation_qty: e.target.value })}
                            placeholder="Ex: 100"
                          />
                        </div>
                        <div style={{ minWidth: '150px' }}>
                          <label className="small text-muted">Valor da Bonificação (R$)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="form-control-modern form-control-sm"
                            value={variableForm.variable_bonus_amount}
                            onChange={(e) => setVariableForm({ ...variableForm, variable_bonus_amount: e.target.value })}
                            placeholder="Ex: 500.00"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Vínculo de Filial */}
                <MultiSelectSearch
                  label="Filiais vinculadas"
                  placeholder="Buscar filial..."
                  value={variableForm.branch_ids}
                  onChange={(selected) => setVariableForm({ ...variableForm, branch_ids: selected })}
                  options={branches}
                  badgeColor="#10b981"
                  getOptionLabel={(opt) => opt.label}
                  getOptionValue={(opt) => opt.codigo}
                />
                <small className="text-muted d-block mb-3" style={{ fontSize: '0.75rem', marginTop: '-8px' }}>
                  Se nenhuma filial for selecionada, a variável estará disponível para todas.
                </small>

                {/* Builder de Fórmula Visual */}
                <div className="form-group-modern mb-3">
                  <label>Fórmula</label>

                  {/* Área de Visualização da Fórmula */}
                  <div
                    style={{
                      background: '#f8fafc',
                      border: '2px dashed #cbd5e1',
                      borderRadius: '12px',
                      padding: '16px',
                      minHeight: '80px',
                      marginBottom: '12px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      alignItems: 'center'
                    }}
                  >
                    {variableForm.formula === '' ? (
                      <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                        Clique nos botões abaixo para montar sua fórmula...
                      </span>
                    ) : (
                      renderFormulaPreview()
                    )}
                  </div>

                  {/* Campo Oculto para o valor real */}
                  <input
                    type="hidden"
                    ref={formulaInputRef}
                    value={variableForm.formula}
                  />

                  {/* Botões de Operadores */}
                  <div className="mb-3">
                    <small className="text-muted d-block mb-2">Operadores:</small>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {['+', '-', '*', '/', '(', ')', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.'].map((op) => (
                        <button
                          key={op}
                          type="button"
                          onClick={() => appendToFormula(op)}
                          style={{
                            padding: '8px 14px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            background: 'white',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                          }}
                          onMouseEnter={(e) => { e.target.style.background = '#f1f5f9'; e.target.style.borderColor = '#cbd5e1'; }}
                          onMouseLeave={(e) => { e.target.style.background = 'white'; e.target.style.borderColor = '#e2e8f0'; }}
                        >
                          {op}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const currentFormula = variableForm.formula;
                          if (currentFormula.length === 0) return;
                          
                          // Função de backspace inteligente
                          const smartBackspace = (formula) => {
                            // Se terminar com uma variável {nome}, remover toda a variável
                            const variableMatch = formula.match(/\{[^}]+\}$/);
                            if (variableMatch) {
                              return formula.slice(0, -variableMatch[0].length);
                            }
                            
                            // Se terminar com um número (incluindo decimal), remover todo o número
                            const numberMatch = formula.match(/\d+(\.\d+)?$/);
                            if (numberMatch) {
                              return formula.slice(0, -numberMatch[0].length);
                            }
                            
                            // Se terminar com um operador, remover apenas o operador
                            const operatorMatch = formula.match(/[\+\-\*\/\(\)]$/);
                            if (operatorMatch) {
                              return formula.slice(0, -1);
                            }
                            
                            // Se não for nenhum dos casos acima, remover último caractere
                            return formula.slice(0, -1);
                          };
                          
                          const newFormula = smartBackspace(currentFormula);
                          setVariableForm({ ...variableForm, formula: newFormula });
                        }}
                        style={{
                          padding: '8px 14px',
                          border: '1px solid #f59e0b',
                          borderRadius: '8px',
                          background: '#fef3c7',
                          color: '#f59e0b',
                          fontWeight: '500',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          marginLeft: '8px'
                        }}
                        title="Backspace inteligente (remove elemento completo)"
                      >
                        <FiDelete size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setVariableForm({ ...variableForm, formula: '' })}
                        style={{
                          padding: '8px 14px',
                          border: '1px solid #ef4444',
                          borderRadius: '8px',
                          background: '#fef2f2',
                          color: '#ef4444',
                          fontWeight: '500',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          marginLeft: '4px'
                        }}
                      >
                        Limpar
                      </button>
                    </div>
                  </div>

                  {/* Variáveis Disponíveis */}
                  <div>
                    <small className="text-muted d-block mb-2">Variáveis disponíveis (clique para adicionar):</small>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {getAvailablePlaceholders().map((placeholder, idx) => {
                        const isVar = placeholder.type === 'variable';
                        const gradient = isVar
                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                        const shadowColor = isVar
                          ? 'rgba(16, 185, 129, 0.3)'
                          : 'rgba(102, 126, 234, 0.3)';
                        const shadowHover = isVar
                          ? 'rgba(16, 185, 129, 0.4)'
                          : 'rgba(102, 126, 234, 0.4)';
                        const labelExtract = placeholder.label.split('(')[1]?.replace(')', '') || '';
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => appendToFormula(`{${placeholder.value}}`)}
                            style={{
                              padding: '10px 16px',
                              border: 'none',
                              borderRadius: '20px',
                              background: gradient,
                              color: 'white',
                              fontWeight: '600',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              boxShadow: `0 2px 8px ${shadowColor}`,
                              transition: 'all 0.15s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = `0 4px 12px ${shadowHover}`; }}
                            onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = `0 2px 8px ${shadowColor}`; }}
                            title={labelExtract}
                          >
                            <FiCode size={14} />
                            {placeholder.value}
                          </button>
                        );
                      })}
                    </div>
                    {getAvailablePlaceholders().length === 0 && (
                      <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '8px', color: '#92400e', fontSize: '0.85rem' }}>
                        Configure filtros ou variáveis na seção abaixo para ter placeholders disponíveis.
                      </div>
                    )}
                  </div>

                  {/* Fórmula em modo texto (toggle) */}
                  <details style={{ marginTop: '12px' }}>
                    <summary style={{ cursor: 'pointer', color: '#64748b', fontSize: '0.85rem' }}>
                      Modo Avançado (editar como texto)
                    </summary>
                    <div style={{ marginTop: '8px' }}>
                      <textarea
                        className="form-control-modern font-monospace"
                        value={variableForm.formula}
                        onChange={(e) => setVariableForm({ ...variableForm, formula: e.target.value })}
                        placeholder="{filter_1} + {filter_2}"
                        rows={2}
                      />
                    </div>
                  </details>
                </div>

                <div className="form-group-modern mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="variableActive"
                      checked={variableForm.active === 1}
                      onChange={(e) => setVariableForm({ ...variableForm, active: e.target.checked ? 1 : 0 })}
                    />
                    <label className="form-check-label" htmlFor="variableActive">
                      Ativo
                    </label>
                  </div>
                </div>

                {/* Dependências: Filtros + Variáveis */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="mb-0">Dependências (Filtros / Variáveis)</label>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn-modern btn-modern-sm btn-modern-secondary"
                        onClick={addFilterToVariable}
                      >
                        <FiPlus size={14} /> Adicionar Filtro
                      </button>
                      <button
                        type="button"
                        className="btn-modern btn-modern-sm btn-modern-secondary"
                        onClick={addVariableDep}
                        style={{ backgroundColor: '#059669', borderColor: '#059669', color: 'white' }}
                      >
                        <FiPlus size={14} /> Adicionar Variável
                      </button>
                    </div>
                  </div>

                  {/* Filtros */}
                  {variableForm.filters.length === 0 && variableForm.variable_deps.length === 0 && (
                    <div className="text-muted text-center py-3" style={{ background: '#f8fafc', borderRadius: '8px' }}>
                      Nenhuma dependência adicionada
                    </div>
                  )}
                  {variableForm.filters.map((filter, index) => (
                    <div
                      key={`f-${index}`}
                      className="d-flex flex-column gap-2 mb-2"
                      style={{
                        background: '#f0f4ff',
                        padding: '12px',
                        borderRadius: '8px',
                        borderLeft: '4px solid #667eea',
                        position: 'relative',
                        zIndex: (variableForm.filters.length - index) + 100
                      }}
                    >
                      <div className="d-flex gap-2 align-items-end">
                        <div style={{ width: '180px', flexShrink: 0 }}>
                          <label className="small text-muted mb-1 d-block">Placeholder</label>
                          <input
                            type="text"
                            className="form-control-modern"
                            style={{ height: '40px' }}
                            value={filter.placeholder}
                            onChange={(e) => updateFilterInVariable(index, 'placeholder', e.target.value)}
                            placeholder="filter_1"
                          />
                        </div>
                        <div className="flex-grow-1" style={{ minWidth: '220px', position: 'relative' }}>
                          <label className="small text-muted mb-1 d-block">Filtro</label>
                          <SingleSelectSearch
                            label=""
                            placeholder="Pesquisar filtro..."
                            value={filter.filter_id}
                            onChange={(val, opt) => updateFilterInVariable(index, 'filter_id', val, opt)}
                            fetchData={fetchFiltersForSelect}
                            options={filters}
                            badgeColor="#667eea"
                            getOptionLabel={(f) => f.name}
                            getOptionValue={(f) => f.id}
                          />
                        </div>
                        <div className="d-flex gap-1" style={{ flexShrink: 0 }}>
                          <button
                            type="button"
                            className="btn-modern btn-modern-light btn-modern-sm"
                            style={{
                              height: '40px',
                              width: '34px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              opacity: index === 0 ? 0.35 : 1,
                              cursor: index === 0 ? 'not-allowed' : 'pointer'
                            }}
                            onClick={() => moveFilterInVariable(index, 'up')}
                            disabled={index === 0}
                            title="Mover filtro para cima"
                          >
                            <FiChevronUp size={18} />
                          </button>
                          <button
                            type="button"
                            className="btn-modern btn-modern-light btn-modern-sm"
                            style={{
                              height: '40px',
                              width: '34px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              opacity: index === variableForm.filters.length - 1 ? 0.35 : 1,
                              cursor: index === variableForm.filters.length - 1 ? 'not-allowed' : 'pointer'
                            }}
                            onClick={() => moveFilterInVariable(index, 'down')}
                            disabled={index === variableForm.filters.length - 1}
                            title="Mover filtro para baixo"
                          >
                            <FiChevronDown size={18} />
                          </button>
                          <button
                            type="button"
                            className="btn-modern btn-modern-light-danger btn-modern-sm"
                            style={{
                              height: '40px',
                              width: '38px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0
                            }}
                            onClick={() => removeFilterFromVariable(index)}
                            title="Remover filtro"
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </div>

                      <div className="d-flex align-items-center justify-content-between pt-2 border-top flex-wrap gap-2" style={{ borderColor: 'rgba(102, 126, 234, 0.18)' }}>
                        <label
                          className="d-inline-flex align-items-center gap-2 mb-0"
                          style={{ cursor: 'pointer', fontSize: '0.82rem', userSelect: 'none' }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                            checked={Boolean(filter.use_goal_filter_config)}
                            onChange={(e) => updateFilterInVariable(index, 'use_goal_filter_config', e.target.checked)}
                          />
                          <span style={{ fontWeight: 600, color: filter.use_goal_filter_config ? '#059669' : '#475569' }}>
                            {filter.use_goal_filter_config ? '⚡ Usar regras exclusivas da meta' : 'Usar regras base do filtro'}
                          </span>
                          <span
                            className="badge"
                            style={{
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              backgroundColor: filter.use_goal_filter_config ? '#d1fae5' : '#f1f5f9',
                              color: filter.use_goal_filter_config ? '#065f46' : '#64748b',
                              border: `1px solid ${filter.use_goal_filter_config ? '#a7f3d0' : '#cbd5e1'}`,
                              borderRadius: '6px',
                              padding: '3px 8px'
                            }}
                          >
                            {filter.use_goal_filter_config ? '⚡ Exclusivo da Meta' : 'Filtro Base'}
                          </span>
                        </label>
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {filter.use_goal_filter_config
                            ? 'Calcula usando regras/metas exclusivas configuradas na Meta do vendedor'
                            : 'Calcula usando regras/metas padrão do filtro cadastrado'}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Dependências de Variáveis */}
                  {variableForm.variable_deps.map((dep, index) => (
                    <div
                      key={`v-${index}`}
                      className="d-flex gap-2 mb-2 align-items-end"
                      style={{
                        background: '#f0fdf4',
                        padding: '12px',
                        borderRadius: '8px',
                        borderLeft: '4px solid #10b981',
                        position: 'relative',
                        zIndex: (variableForm.variable_deps.length - index) + 5
                      }}
                    >
                      <div style={{ width: '180px', flexShrink: 0 }}>
                        <label className="small text-muted mb-1 d-block">Placeholder</label>
                        <input
                          type="text"
                          className="form-control-modern"
                          style={{ height: '40px' }}
                          value={dep.placeholder}
                          onChange={(e) => updateVariableDep(index, 'placeholder', e.target.value)}
                          placeholder="var_1"
                        />
                      </div>
                      <div className="flex-grow-1" style={{ minWidth: '220px', position: 'relative' }}>
                        <label className="small text-muted mb-1 d-block">Variável</label>
                        <SingleSelectSearch
                          label=""
                          placeholder="Pesquisar variável..."
                          value={dep.depends_on_variable_id}
                          onChange={(val, opt) => updateVariableDep(index, 'depends_on_variable_id', val, opt)}
                          fetchData={fetchVariablesForSelect}
                          options={allVariables
                            .filter(v => v.id !== variableForm.id)
                            .filter(v => {
                              if (variableForm.branch_ids.length === 0) return true;
                              const vBranches = (v.filter_branch || '').split(',').map(s => s.trim()).filter(Boolean);
                              return vBranches.length === 0 || vBranches.some(code => variableForm.branch_ids.includes(code));
                            })
                          }
                          badgeColor="#10b981"
                          getOptionLabel={(v) => v.name}
                          getOptionValue={(v) => v.id}
                        />
                      </div>
                      <div className="d-flex gap-1" style={{ flexShrink: 0 }}>
                        <button
                          type="button"
                          className="btn-modern btn-modern-light btn-modern-sm"
                          style={{
                            height: '40px',
                            width: '34px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            opacity: index === 0 ? 0.35 : 1,
                            cursor: index === 0 ? 'not-allowed' : 'pointer'
                          }}
                          onClick={() => moveVariableDepInVariable(index, 'up')}
                          disabled={index === 0}
                          title="Mover variável para cima"
                        >
                          <FiChevronUp size={18} />
                        </button>
                        <button
                          type="button"
                          className="btn-modern btn-modern-light btn-modern-sm"
                          style={{
                            height: '40px',
                            width: '34px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            opacity: index === variableForm.variable_deps.length - 1 ? 0.35 : 1,
                            cursor: index === variableForm.variable_deps.length - 1 ? 'not-allowed' : 'pointer'
                          }}
                          onClick={() => moveVariableDepInVariable(index, 'down')}
                          disabled={index === variableForm.variable_deps.length - 1}
                          title="Mover variável para baixo"
                        >
                          <FiChevronDown size={18} />
                        </button>
                        <button
                          type="button"
                          className="btn-modern btn-modern-light-danger btn-modern-sm"
                          style={{
                            height: '40px',
                            width: '38px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                          }}
                          onClick={() => removeVariableDep(index)}
                          title="Remover variável"
                        >
                          <FiTrash2 size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '20px 28px 24px',
                borderTop: '1px solid #f1f5f9',
                display: 'flex', justifyContent: 'flex-end', gap: '12px'
              }}>
                <button
                  type="button"
                  className="btn-modern btn-modern-light"
                  onClick={() => { setShowVariableModal(false); resetVariableForm(); }}
                >
                  {canManage ? 'Cancelar' : 'Fechar'}
                </button>
                {canManage && (
                  <button
                    type="submit"
                    className="btn-modern btn-modern-primary"
                    disabled={loading}
                    style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1' }}
                  >
                    {loading ? 'Salvando...' : variableForm.id ? 'Atualizar' : <><FiSave size={16} className="me-1" />Salvar</>}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showBulkDeleteModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
          onClick={(e) => e.target === e.currentTarget && setShowBulkDeleteModal(false)}
        >
          <div style={{
            background: 'white', borderRadius: '20px',
            width: '100%', maxWidth: '450px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '32px 28px 20px', textAlign: 'center' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: '#fee2e2', color: '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <FiTrash2 size={32} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>
                Excluir Variáveis
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                Tem certeza que deseja excluir <strong>{selectedVarIds.length}</strong> variáveis selecionadas?
              </p>
            </div>

            <div style={{ padding: '0 28px 20px', textAlign: 'center' }}>
              <div style={{ padding: '12px 16px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '0.85rem', color: '#92400e' }}>
                ⚠️ Esta ação não pode ser desfeita e removerá permanentemente as variáveis.
              </div>
            </div>

            <div style={{ padding: '16px 28px 24px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button
                type="button"
                className="btn-modern btn-modern-secondary"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={bulkDeleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-modern btn-modern-danger"
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
              >
                <FiTrash2 className="me-2" size={16} />
                {bulkDeleting ? 'Excluindo...' : 'Sim, Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && variableToDelete && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
          onClick={(e) => e.target === e.currentTarget && setShowDeleteModal(false)}
        >
          <div style={{
            background: 'white', borderRadius: '20px',
            width: '100%', maxWidth: '450px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease',
            overflow: 'hidden'
          }}>
            {/* Ícone de Aviso */}
            <div style={{
              padding: '32px 28px 20px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: '#fee2e2', color: '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <FiTrash2 size={32} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>
                Confirmar Exclusão
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                Tem certeza que deseja excluir a variável
              </p>
              <p style={{ margin: '8px 0 0', fontWeight: 600, fontSize: '1rem', color: '#1e293b' }}>
                "{variableToDelete.name}"?
              </p>
            </div>

            {/* Alerta */}
            <div style={{
              padding: '0 28px 20px',
              textAlign: 'center'
            }}>
              <div style={{
                padding: '12px 16px',
                background: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#92400e'
              }}>
                ⚠️ Esta ação não pode ser desfeita. Todos os dados da variável serão removidos.
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 28px 24px',
              display: 'flex', justifyContent: 'center', gap: '12px'
            }}>
              <button
                type="button"
                className="btn-modern btn-modern-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-modern btn-modern-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                <FiTrash2 className="me-2" size={16} />
                {deleting ? 'Excluindo...' : 'Sim, Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Erro */}
      {showErrorModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(4px)', padding: '20px'
          }}
          onClick={(e) => e.target === e.currentTarget && setShowErrorModal(false)}
        >
          <div style={{
            background: 'white', borderRadius: '20px',
            width: '100%', maxWidth: '450px',
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.25s ease',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '32px 28px 20px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: '#fee2e2', color: '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <FiAlertTriangle size={32} />
              </div>
              <h4 style={{ margin: '0 0 8px', fontWeight: 700, fontSize: '1.25rem', color: '#1e293b' }}>
                {errorModalTitle}
              </h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', lineHeight: '1.5' }}>
                {errorMessage}
              </p>
            </div>

            <div style={{
              padding: '16px 28px 24px',
              display: 'flex', justifyContent: 'center', gap: '12px'
            }}>
              <button
                type="button"
                className="btn-modern btn-modern-primary"
                onClick={() => setShowErrorModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dropdown para substituição de variáveis na fórmula */}
      {showVariableReplaceDropdown && selectedVariableToReplace && (
        <div
          style={{
            position: 'fixed',
            left: variableReplacePosition.x,
            top: variableReplacePosition.y,
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            zIndex: 10000,
            minWidth: '250px',
            maxHeight: '300px',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #f1f5f9',
            background: '#f8fafc',
            borderRadius: '12px 12px 0 0'
          }}>
            <div style={{
              fontSize: '0.85rem',
              fontWeight: '600',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FiCode size={14} />
              Substituir "{selectedVariableToReplace}"
            </div>
          </div>
          
          <div style={{ padding: '8px 0' }}>
            {getAvailablePlaceholders()
              .filter(p => p.value !== selectedVariableToReplace) // Excluir a variável atual
              .map((placeholder, idx) => {
                const filterName = placeholder.label.split('(')[1]?.replace(')', '') || '';
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => replaceVariableInFormula(selectedVariableToReplace, placeholder.value)}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '0.85rem',
                      color: '#374151',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#f1f5f9'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    title={filterName}
                  >
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      {placeholder.value.slice(0, 2).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500' }}>{placeholder.value}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{filterName}</div>
                    </div>
                  </button>
                );
              })}
            
            {getAvailablePlaceholders().filter(p => p.value !== selectedVariableToReplace).length === 0 && (
              <div style={{
                padding: '16px',
                textAlign: 'center',
                color: '#64748b',
                fontSize: '0.85rem'
              }}>
                Nenhuma outra variável disponível
              </div>
            )}
          </div>
          
          <div style={{
            padding: '8px 16px',
            borderTop: '1px solid #f1f5f9',
            background: '#f8fafc',
            borderRadius: '0 0 12px 12px'
          }}>
            <button
              type="button"
              onClick={() => {
                setShowVariableReplaceDropdown(false);
                setSelectedVariableToReplace(null);
              }}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #e2e8f0',
                background: 'white',
                borderRadius: '6px',
                fontSize: '0.8rem',
                color: '#64748b',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Overlay para fechar dropdown quando clicar fora */}
      {showVariableReplaceDropdown && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999
          }}
          onClick={() => {
            setShowVariableReplaceDropdown(false);
            setSelectedVariableToReplace(null);
          }}
        />
      )}

      <style>{`
        .spin-ico { animation: spin-ico 0.8s linear infinite; display: inline-block; }
        @keyframes spin-ico { to { transform: rotate(360deg); } }
        @keyframes modalSlideIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

export default Variaveis;
